import {
  AmbientLight,
  Color,
  EquirectangularReflectionMapping,
  Group,
  NoToneMapping,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PMREMGenerator,
  Scene,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  WebGLRenderer,
} from "three";
import { Ioc } from "@core/ioc/ioc";
import { FrameModel, FRAME_MODEL } from "@core/frame/frame";
import {
  beginWorkflowPerformanceOperation,
  writePerformanceDiagnostic,
} from "@models/project/SceneNavigationDiagnosticLogger";
import { PickingService, PICKING_SERVICE } from "@runtime/picking/pickingService";
import {
  MAX_EFFECTIVE_PIXEL_RATIO,
  MIN_EFFECTIVE_PIXEL_RATIO,
  RENDER_QUALITY_DEFAULTS,
  RENDER_QUALITY_MODEL,
  type RenderQualityModel,
} from "@runtime/engine/renderQuality";

import { publicUrl } from "@core/publicUrl";
const MAX_DEVICE_PIXEL_RATIO = 2;
/** A frame this slow is worth a line even when it created no GPU resources. */
const K_FRAME_RENDER_LOG_THRESHOLD_MS = 16;
const CAMPFIRE_SKYBOX_URL = publicUrl("environment/005_studioHDRI.jpg");
// Unity's `Environment.prefab` has NO constant ambient term: its ambient is the
// skybox (Main.unity, ambient mode Skybox, intensity 1), which on web is the
// PMREM'd studio map below. The flat 0.2 that used to sit here was dialled by
// eye against a pipeline with no sRGB output transfer and π-weak direct
// lights (see `COLOR_PIPELINE.md` §3, 2026-09-03); with both corrected it only
// lifted every shadowed face off the floor. Kept as a dial rather than
// deleted — a re-dial, if one is ever wanted, is one number.
const CAMPFIRE_AMBIENT_LIGHT_INTENSITY = 0;

/**
 * The IBL environment ships as an 8-bit sRGB JPEG (425 KB) rather than the
 * 8.9 MB `.hdr` it was authored as — the whole map was re-downloaded on every
 * cold load for a light source nothing ever sees directly.
 *
 * It is a Photoshop-made studio HDRI with almost no dynamic range: peak
 * radiance is 1.83 and the 99.9th percentile is 1.20, so the file was paying
 * RGBE's 4-bytes-per-pixel for a range a byte can carry. It is encoded as
 * `sRGB(linear / 2)` — halved so the handful of pixels above 1.0 survive
 * instead of clipping — and the halving is undone by the encode scale below.
 * PMREM is linear, so the resulting cubemap matches the `.hdr` to ~0.2 % of
 * total irradiance.
 *
 * TWO THINGS MUST HOLD, and both fail silently:
 *  - `flipY = true` on the loaded texture — the image-texture default, and
 *    ⚠ what the `.hdr` path it replaced ALSO used: `RGBELoader` is a shim
 *    over `HDRLoader`, whose load callback forces `texture.flipY = true` for
 *    its half-float output (`HDRLoader.js`, `onLoadCallback`). The JPEG's
 *    rows are in the `.hdr`'s own top-to-bottom scanline order and three's
 *    `equirectUv` puts +Y at v = 1, so a `false` here uploads the studio map
 *    UPSIDE DOWN — ceiling softboxes below the horizon, the floor above it.
 *    That shipped from 2026-08-18 to 2026-09-03 on the strength of a comment
 *    that claimed the loader's default was `false`; it was not. Do not
 *    re-derive this from `DataTexture`'s default — that class's `false` is
 *    what the loader OVERRIDES.
 *  - Resolution stays 4096 wide. `PMREMGenerator._fromTexture` derives its
 *    cube size as `image.width / 4`, so a smaller source is not just a
 *    smaller download, it is a blurrier reflection.
 */
const CAMPFIRE_SKYBOX_ENCODE_SCALE = 2;

/**
 * Multiplier on the image-based lighting, applied on top of the encode scale.
 * `1` is the authored map at unit strength, which is what Unity renders:
 * `DefaultSkybox.mat` `_Tint` 0.5019 (sRGB → 0.2158 linear) × the skybox
 * shader's `unity_ColorSpaceDouble` (4.5948) = 0.99× the HDRI, ambient mode
 * Skybox at intensity 1, reflection probe intensity 1 — no boost anywhere.
 *
 * ⚠ It shipped at **1.6** from 2026-08-18 to 2026-09-03, "chosen by eye
 * against Unity". That eye was right about the symptom and wrong about the
 * cause: the scene read flat because the viewport skipped the sRGB output
 * transfer (mid-tones at 40–70 %), the direct lights were π× too weak, and the
 * map itself was uploaded upside down — three defects that a 1.6× ambient
 * boost partly papered over and partly made worse (it is exactly what
 * flattened the direct-light modelling). All three are fixed; the dial goes
 * back to the value Unity uses. The dev-stats slider still overrides it.
 *
 * NOTE this lands on `scene.environmentIntensity`, which three does not
 * multiply into `material.envMapIntensity` — it OVERWRITES it for any
 * standard/lambert/phong material with no `envMap` of its own
 * (`WebGLRenderer.setProgram`). Every Campfire material is in that class, so
 * this is the single global lever, and the per-material constants in
 * `sceneEnvironmentRenderer` / `campfireMaterialBuilder` are already inert.
 */
export const DEFAULT_SKYBOX_LIGHTING = 1;
export const MIN_SKYBOX_LIGHTING = 0;
export const MAX_SKYBOX_LIGHTING = 5;

/** Dev-only override for {@link DEFAULT_SKYBOX_LIGHTING}, set from the stats panel. */
const SKYBOX_LIGHTING_PREF_KEY = "Campfire.Dev.SkyboxLighting";

/**
 * Frames drawn per {@link ThreeEngine.requestRender}: the frame the change is
 * made in, and the next, which picks up what the late tick applied after the
 * first one was drawn (`sceneEntityRenderer`'s batch flush, a texture arrival).
 */
const K_FRAMES_PER_REQUEST = 2;
/**
 * rAF timestamps land on vsync, so "33.3 ms since the last draw" arrives as
 * 33.2 or 33.4. The slack keeps the ambient rate from skipping a whole vsync
 * on the low side — at 60 Hz that would halve 30 fps to 20.
 */
const K_AMBIENT_SLACK_MS = 2;
/**
 * While nothing has asked for a frame, draw one this often anyway — a safety
 * net (user direction, 2026-10-01) so a change no trigger covers (a texture
 * that arrives with no event) shows up within a second instead of waiting for
 * the next interaction.
 */
const K_IDLE_HEARTBEAT_MS = 1000;

/**
 * How often a continuously animating source needs the viewport redrawn under
 * render on demand. `full` is every frame. `ambient` is capped at
 * `RenderQualitySettings.ambientFps` (30 by default) — for motion that reads
 * the same at a low rate and would otherwise keep the viewport drawing at the
 * display's full rate on its own (Spark's idle breathing).
 */
export type ContinuousRenderRate = "full" | "ambient";

export interface ContinuousRenderSource {
  readonly rate: ContinuousRenderRate;
  /** What the stats panel's "Drawing for" row calls it — "camera", "Spark idle". */
  readonly label: string;
  /** True while the source changes the picture from one frame to the next. */
  isActive(): boolean;
}

export interface ThreeEngine {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly root: Group;
  camera: PerspectiveCamera | null;
  setRenderPipeline(render: (() => void) | null): void;
  addResizeListener(listener: () => void): () => void;
  /**
   * The picture is about to change: draw this frame and the next, even under
   * render on demand. Cheap and idempotent — call it from any handler that
   * mutates something the viewport draws. The second frame is for work the
   * late tick does AFTER this frame's draw (batch flushes, texture applies).
   */
  requestRender(): void;
  /**
   * Keep drawing while `source.isActive()` — for anything that changes the
   * picture every frame for a while (an animation clip, a camera flight, a
   * pulse). Returns the unregister.
   */
  addContinuousRenderSource(source: ContinuousRenderSource): () => void;
  /**
   * Diagnostics: how many frames DREW since the last call, by why — a source's
   * label, "request", "settle", "heartbeat", or "every frame" with render on
   * demand off. Taking them resets the count. Read by the stats panel only.
   */
  takeDrawReasons(): ReadonlyMap<string, number>;
  /**
   * An opaque surface covers the viewport right now (a guided run's
   * media-only step), so nothing drawn would be seen. Render on demand's
   * sources read it to stop asking for frames; it never stops a draw itself.
   */
  isViewportCovered(): boolean;
}

/**
 * EngineService owns the Three.js renderer, scene root, resize lifecycle, and
 * Campfire frame ticks for the web viewport.
 *
 * ## Color mode
 *
 * **See `COLOR_PIPELINE.md` in this folder — read it before touching any color
 * anywhere in the app.** In brief: the viewport renders through an
 * `EffectComposer` whose chain has no `OutputPass`, so what reaches the screen
 * is the un-encoded LINEAR buffer. That is the reference every other surface is
 * matched to (direction call 2026-08-06: match the previews to the viewport,
 * not the other way round), and `outputColorSpace` below is what aligns the two
 * surfaces three lets it reach — the sketch screenshot (direct-to-canvas) and
 * WebXR (whose framebuffer colorSpace is stamped from it at session start).
 *
 * The consequence, and the open decision, is in the doc: lit content displays
 * at 20–70 % of its intended brightness, while the hand-authored chrome colors
 * are calibrated to exactly this mode. Do NOT flip this line on its own.
 *
 * ## Render on demand
 *
 * With `RenderQualitySettings.renderOnDemand` on (the default since
 * 2026-10-07; the stats panel can turn it off), the frame loop still TICKS
 * every frame but only DRAWS when something asked for it: a
 * {@link ThreeEngine.requestRender} (two frames), a
 * {@link ContinuousRenderSource} that is active (every frame, or
 * `RenderQualitySettings.ambientFps` for an `ambient` one), or the
 * {@link K_IDLE_HEARTBEAT_MS} safety frame. Every frame that draws is counted
 * by why ({@link ThreeEngine.takeDrawReasons}) for the stats panel. The draw is
 * ~26 full-screen passes whatever the scene holds, so a still scene that keeps
 * drawing it at 60–120 Hz is pure GPU load — enough to starve video decode on
 * an integrated GPU.
 *
 * Ticking never stops, deliberately: several renderers build their visuals in
 * `onTick` (billboards, rulers, the step leader line, highlight pulses), and
 * `deltaSeconds` stays one frame long, so nothing jumps when drawing resumes.
 * The canvas keeps its last frame between draws (`preserveDrawingBuffer`).
 *
 * ⚠ A source that changes the picture and neither requests a frame nor
 * registers as continuous shows up only on the next heartbeat. An XR session
 * always draws every frame — the head pose moves with no event.
 */
export class EngineService {
  private m_engine: ThreeEngine | null = null;
  private m_frame: FrameModel | null = null;
  private m_lastFrameMs = 0;
  private m_environment: Texture | null = null;
  private m_pmrem: PMREMGenerator | null = null;
  private m_renderPipeline: (() => void) | null = null;
  private m_skyboxLighting = readSkyboxLightingPref();
  private m_renderQuality: RenderQualityModel | null = null;
  private m_offRenderQualityChanged: (() => void) | null = null;
  private readonly m_resizeListeners = new Set<() => void>();
  private m_disposed = false;
  /** See "Render on demand" above. */
  private m_renderOnDemand = RENDER_QUALITY_DEFAULTS.renderOnDemand;
  private m_requestedFrames = 0;
  private readonly m_continuousSources = new Set<ContinuousRenderSource>();
  private m_lastDrawMs = 0;
  /** Whether any continuous source was active at the last draw decision. */
  private m_wasContinuousActive = false;
  /** `RenderQualitySettings.ambientFps` — see {@link ContinuousRenderRate}. 0 = every frame. */
  private m_ambientFps = 30;
  /** See {@link setViewportCoveredProbe}. */
  private m_viewportCovered: (() => boolean) | null = null;
  /** See {@link ThreeEngine.takeDrawReasons}. */
  private m_drawReasons = new Map<string, number>();
  /** See {@link setSceneSuspended}. */
  private m_sceneSuspended = false;

  initialize(canvas: HTMLCanvasElement): { app: ThreeEngine; root: Group } {
    if (this.m_engine) {
      throw new Error("EngineService already initialized");
    }
    this.m_disposed = false;

    const renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      preserveDrawingBuffer: true,
      stencil: true,
    });
    // ONE output color mode for the whole app — `COLOR_PIPELINE.md`, §3:
    // colour-managed, sRGB out, as of 2026-09-03. Consulted when three draws
    // to the default framebuffer (the sketch screenshot) or to the XR
    // framebuffer; the composer applies the same transfer through its own
    // `OutputPass` (`SelectionOutlineController`), and offscreen capture
    // targets apply it on read-back (`previewCaptureCore`). All four surfaces
    // encode the same way, which is what keeps them agreeing. Do NOT change
    // this line on its own — §4 of that document is the all-or-nothing list.
    renderer.outputColorSpace = SRGBColorSpace;
    // Per-material clipping planes (cutting-plane tool). Renderer-global
    // `clippingPlanes` is deliberately NOT used — Unity's `_CAMPFIRE_CLIPPING`
    // keyword only cuts project materials, never the environment set /
    // avatars / media tiles (see `runtime/tools/SlicePlaneRenderer.ts`).
    renderer.localClippingEnabled = true;
    renderer.toneMapping = NoToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = PCFSoftShadowMap;
    // A DISPLAY colour, declared as the sRGB input it is (`COLOR_PIPELINE.md`
    // Population B): decoded here, encoded back by the output transfer.
    renderer.setClearColor(new Color().setRGB(0.04, 0.04, 0.045, SRGBColorSpace), 1);
    if (Ioc.isRegistered<RenderQualityModel>(RENDER_QUALITY_MODEL)) {
      this.m_renderQuality = Ioc.resolve<RenderQualityModel>(RENDER_QUALITY_MODEL);
      // A scale change is a resize of every buffer in the chain, so it goes
      // through the same path a window resize does — that is also what pushes
      // the new pixel ratio into the composer (`SelectionOutlineController`).
      // The model also carries the AA mode, which costs nothing to change, so
      // filter on the scale rather than reallocating every target on it.
      let lastScale = this.m_renderQuality.settings.renderScale;
      this.m_renderOnDemand = this.m_renderQuality.settings.renderOnDemand;
      this.m_ambientFps = this.m_renderQuality.settings.ambientFps;
      this.m_offRenderQualityChanged = this.m_renderQuality.onChanged.on((settings) => {
        const { renderScale, renderOnDemand, ambientFps } = settings;
        this.m_renderOnDemand = renderOnDemand;
        this.m_ambientFps = ambientFps;
        // Any of these changes the picture — the AA mode included, which
        // `SelectionOutlineController` swaps without a resize.
        this.requestRender();
        if (renderScale === lastScale) return;
        lastScale = renderScale;
        this.handleResize();
      });
    }
    renderer.setPixelRatio(this.canvasPixelRatio());

    const scene = new Scene();
    scene.background = new Color().setRGB(0.04, 0.04, 0.045, SRGBColorSpace);
    scene.environmentIntensity = CAMPFIRE_SKYBOX_ENCODE_SCALE * this.m_skyboxLighting;
    scene.add(new AmbientLight(new Color(1, 1, 1), CAMPFIRE_AMBIENT_LIGHT_INTENSITY));
    const root = new Group();
    root.name = "campfire.root";
    scene.add(root);

    this.m_engine = {
      canvas,
      renderer,
      scene,
      root,
      camera: null,
      setRenderPipeline: (render) => {
        this.m_renderPipeline = render;
        this.requestRender();
      },
      addResizeListener: (listener) => {
        this.m_resizeListeners.add(listener);
        return () => this.m_resizeListeners.delete(listener);
      },
      requestRender: () => this.requestRender(),
      addContinuousRenderSource: (source) => {
        this.m_continuousSources.add(source);
        return () => {
          this.m_continuousSources.delete(source);
          // Its last frame may have been mid-motion; settle on the final state.
          this.requestRender();
        };
      },
      takeDrawReasons: () => {
        const taken = this.m_drawReasons;
        this.m_drawReasons = new Map();
        return taken;
      },
      // A headset sees the scene whatever the page shows.
      isViewportCovered: () =>
        !renderer.xr.isPresenting && (this.m_viewportCovered?.() ?? false),
    };
    this.m_frame = Ioc.resolve<FrameModel>(FRAME_MODEL);
    this.m_pmrem = new PMREMGenerator(renderer);
    this.m_pmrem.compileEquirectangularShader();

    if (Ioc.isRegistered<PickingService>(PICKING_SERVICE)) {
      Ioc.resolve<PickingService>(PICKING_SERVICE).bindApp(this.m_engine);
    }

    void this.configureCampfireEnvironment();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
    this.m_lastFrameMs = performance.now();
    // Drive the frame loop through the renderer rather than raw
    // `requestAnimationFrame`: while a WebXR session is presenting, three's
    // WebXRManager reroutes this callback onto the XR session's frame clock
    // (the only way to render inside the headset). Outside a session it is
    // plain rAF with identical behavior.
    renderer.setAnimationLoop(this.renderFrame);

    return { app: this.m_engine, root };
  }

  setCamera(camera: PerspectiveCamera): void {
    if (!this.m_engine) throw new Error("EngineService not initialized");
    this.m_engine.camera = camera;
    this.handleResize();
  }

  /**
   * Who answers {@link ThreeEngine.isViewportCovered} — the viewport, which
   * owns the cover a guided run raises over a media-only step. `null` on
   * unmount. A probe rather than a pushed flag so it can never go stale.
   */
  setViewportCoveredProbe(probe: (() => boolean) | null): void {
    this.m_viewportCovered = probe;
  }

  /**
   * Stop the scene's frame loop — the `FrameModel` tick, the draw and the late
   * tick — while an opaque surface covers the viewport, and start it again.
   * Today that surface is the empty stage a guided run shows behind a
   * media-only step (`ViewportView`, `IWorkflowRunModel.isSceneHidden`).
   *
   * Why stop rather than just keep drawing what nobody can see: the draw is
   * about 26 full-screen post-processing passes a frame whatever the scene
   * holds, and on an integrated GPU that starves the hardware decoder of the
   * step's own video — it stalled ~3.3 s into every play for 4–6 s (measured
   * 2026-10-01, Intel Arc iGPU). Reverting the hidden scene's effects removes
   * ~4 % of that work; stopping the loop removes all of it.
   *
   * What it leaves:
   *   - The canvas keeps its last frame (`preserveDrawingBuffer`), under the
   *     cover; a resize while suspended clears it, which the cover also hides.
   *   - The frame clock stays current, so the first tick back is handed one
   *     frame's delta rather than the whole time away — animations, morphs and
   *     camera moves resume where they stopped instead of jumping to the end.
   *   - Resizes still apply, so the first frame back is the right size.
   *   - Under render on demand, resuming asks for a frame, so the first frame
   *     back draws even when nothing changed while the scene was covered.
   *   - ⚠ An XR session ignores it: the cover is DOM and the wearer still sees
   *     the scene, so a headset keeps ticking and drawing.
   *
   * ⚠ Nothing that must keep running while the scene is covered may live on
   * the tick — the media step's own DOM layout in particular
   * (`ViewportView`'s free-area publish runs on its own loop while this is set).
   */
  setSceneSuspended(suspended: boolean): void {
    if (this.m_sceneSuspended && !suspended) this.requestRender();
    this.m_sceneSuspended = suspended;
  }

  private canvasPixelRatio(): number {
    return getCanvasPixelRatio(this.m_renderQuality?.settings.renderScale ?? 1);
  }

  private readonly handleResize = (): void => {
    const engine = this.m_engine;
    if (!engine) return;
    // While an XR session presents, three owns the drawing-buffer size; a
    // window resize (which Quest's browser fires on immersive entry) must
    // not fight it. The desktop size is restored by three on session end.
    if (engine.renderer.xr.isPresenting) return;

    const width = Math.max(1, Math.round(engine.canvas.clientWidth));
    const height = Math.max(1, Math.round(engine.canvas.clientHeight));
    engine.renderer.setPixelRatio(this.canvasPixelRatio());
    engine.renderer.setSize(width, height, false);
    if (engine.camera) {
      engine.camera.aspect = width / height;
      engine.camera.updateProjectionMatrix();
    }
    for (const listener of this.m_resizeListeners) listener();
    // `setSize` clears the drawing buffer — under render on demand the canvas
    // would stay blank until something else asked for a frame.
    this.requestRender();
  };

  private requestRender(): void {
    this.m_requestedFrames = Math.max(this.m_requestedFrames, K_FRAMES_PER_REQUEST);
  }

  /**
   * See "Render on demand" in the class doc. Called once per frame, after the
   * tick. Returns WHY this frame draws (the stats panel's "Drawing for"), or
   * null when it does not.
   */
  private drawReason(nowMs: number, engine: ThreeEngine): string | null {
    if (!this.m_renderOnDemand) return "every frame";
    if (engine.renderer.xr.isPresenting) return "XR session";

    // Unthrottled, an ambient source is simply a full-rate one.
    let full: ContinuousRenderSource | null = null;
    let ambient: ContinuousRenderSource | null = null;
    for (const source of this.m_continuousSources) {
      if (!source.isActive()) continue;
      if (source.rate === "full" || this.m_ambientFps <= 0) {
        full = source;
        break;
      }
      ambient ??= source;
    }
    // A motion's LAST step lands on the frame its source first reports
    // inactive (a flight reaching its pose, a clip finishing), so the frame
    // after the last active one is drawn too or the final pose never shows.
    const settling = this.m_wasContinuousActive && full === null && ambient === null;
    this.m_wasContinuousActive = full !== null || ambient !== null;

    if (this.m_requestedFrames > 0) {
      this.m_requestedFrames--;
      return "request";
    }
    if (full) return full.label;
    if (settling) return "settle";
    const sinceDrawMs = nowMs - this.m_lastDrawMs;
    if (ambient && sinceDrawMs >= 1000 / this.m_ambientFps - K_AMBIENT_SLACK_MS) {
      return `${ambient.label} (${this.m_ambientFps} fps)`;
    }
    return sinceDrawMs >= K_IDLE_HEARTBEAT_MS ? "heartbeat" : null;
  }

  private readonly renderFrame = (nowMs: number): void => {
    if (this.m_disposed) return;
    const engine = this.m_engine;
    const deltaSeconds = Math.max(0, (nowMs - this.m_lastFrameMs) / 1000);
    this.m_lastFrameMs = nowMs;
    // After the clock update, so a resume starts from one frame's delta.
    if (this.m_sceneSuspended && !engine?.renderer.xr.isPresenting) return;
    this.m_frame?.tick(deltaSeconds);
    const reason = engine?.camera ? this.drawReason(nowMs, engine) : null;
    if (engine?.camera && reason !== null) {
      this.m_lastDrawMs = nowMs;
      this.m_drawReasons.set(reason, (this.m_drawReasons.get(reason) ?? 0) + 1);
      const usesComposer = Boolean(
        this.m_renderPipeline && !engine.renderer.xr.isPresenting,
      );
      const endRender = beginWorkflowPerformanceOperation("frame render", {
        pipeline: usesComposer ? "composer" : "direct",
        xrPresenting: engine.renderer.xr.isPresenting,
      });
      // Three creates a shader program and uploads a texture the first time an
      // object is actually DRAWN, so an object that has only ever been hidden
      // owns no GPU resources. These counters are what separates "the reveal
      // did heavy CPU work" from "the reveal paid every deferred compile and
      // upload at once": `programs` is the program cache, and `memory.*` are
      // the live GPU allocations, neither of which `info.reset()` touches.
      const info = engine.renderer.info;
      const programsBefore = info.programs?.length ?? 0;
      const texturesBefore = info.memory.textures;
      const geometriesBefore = info.memory.geometries;
      const renderStartedAt = performance.now();
      // While an XR session presents, bypass any installed render pipeline:
      // `EffectComposer` (selection outlines) renders into screen-sized
      // targets and cannot drive the stereo XR framebuffer. Direct
      // `renderer.render` lets WebXRManager substitute its ArrayCamera.
      try {
        if (usesComposer) {
          this.renderPipelineWithOneMatrixWalk(engine, this.m_renderPipeline!);
        } else {
          engine.renderer.render(engine.scene, engine.camera);
        }
      } finally {
        endRender();
        this.reportFrameGpuWork(
          performance.now() - renderStartedAt,
          (info.programs?.length ?? 0) - programsBefore,
          info.memory.textures - texturesBefore,
          info.memory.geometries - geometriesBefore,
          usesComposer,
        );
      }
    }
    this.m_frame?.lateTick();
  };

  /**
   * Runs the pipeline with ONE walk of the stage's matrices for the whole frame.
   *
   * Every `renderer.render(scene)` starts with `scene.updateMatrixWorld()`,
   * which recomposes the local and world matrix of every node in the graph,
   * visible or not: three keeps no record of what moved, so it recomputes
   * everything to be safe. The pipeline renders this scene several times a
   * frame — the beauty render, N8AO's two translucent-object renders, two per
   * active outline pass — and nothing moves between those renders, so each one
   * after the first recomputed the same matrices: 3 walks a frame idle, 7 with
   * a hover and a selection, each over every node of every visited scene.
   *
   * So the frame walks once, here, after the tick has moved everything it is
   * going to move, and the renders inside skip the STAGE (`engine.root`, every
   * project node) and re-walk only the room around it: the camera, the
   * gizmos, Spark, the XR rig, about 200 nodes. That remainder is kept
   * deliberately: `TransformControls` re-shows its handles inside its own
   * `updateMatrixWorld`, so an outline pass that hides everything but the
   * selection for its mask render gets the handles back, and the outlines
   * have always traced them. Skipping the whole walk drops those traces
   * (5,765 px on Broach Cell, 2026-10-07): a look change, not this one's to
   * make.
   *
   * A render of a subtree as its own scene (the highlight pass's mask) still
   * walks that subtree, unchanged. Anything that moves a stage node DURING the
   * pipeline would be drawn where it was for that frame — nothing does today;
   * keep it that way, or have that change update its own matrices.
   */
  private renderPipelineWithOneMatrixWalk(engine: ThreeEngine, render: () => void): void {
    const scene = engine.scene;
    const stage = engine.root;
    if (!scene.matrixWorldAutoUpdate || Object.prototype.hasOwnProperty.call(stage, "updateMatrixWorld")) {
      render();
      return;
    }
    scene.updateMatrixWorld();
    stage.updateMatrixWorld = skipMatrixWalk;
    try {
      render();
    } finally {
      delete (stage as { updateMatrixWorld?: unknown }).updateMatrixWorld;
    }
  }

  private reportFrameGpuWork(
    durationMs: number,
    programsCreated: number,
    texturesUploaded: number,
    geometriesUploaded: number,
    usesComposer: boolean,
  ): void {
    const createdAnything =
      programsCreated > 0 || texturesUploaded > 0 || geometriesUploaded > 0;
    if (!createdAnything && durationMs < K_FRAME_RENDER_LOG_THRESHOLD_MS) return;
    writePerformanceDiagnostic("FrameGpuWork", "frame render", {
      durationMs: Math.round(durationMs * 10) / 10,
      programsCreated,
      texturesUploaded,
      geometriesUploaded,
      pipeline: usesComposer ? "composer" : "direct",
    });
  }

  dispose(): void {
    this.m_disposed = true;
    this.m_sceneSuspended = false;
    this.m_offRenderQualityChanged?.();
    this.m_offRenderQualityChanged = null;
    this.m_renderQuality = null;
    window.removeEventListener("resize", this.handleResize);
    this.m_engine?.renderer.setAnimationLoop(null);
    this.clearCampfireEnvironment();
    this.m_resizeListeners.clear();
    this.m_continuousSources.clear();
    this.m_requestedFrames = 0;
    this.m_wasContinuousActive = false;
    this.m_drawReasons = new Map();
    this.m_viewportCovered = null;
    this.m_renderPipeline = null;
    this.m_engine?.renderer.dispose();
    this.m_engine?.renderer.forceContextLoss();
    this.m_engine = null;
    this.m_frame = null;
  }

  get app(): ThreeEngine {
    if (!this.m_engine) throw new Error("EngineService not initialized");
    return this.m_engine;
  }

  get root(): Group {
    if (!this.m_engine) throw new Error("EngineService not initialized");
    return this.m_engine.root;
  }

  /** {@link root}, or `null` before a viewport has initialized the engine. */
  get rootOrNull(): Group | null {
    return this.m_engine?.root ?? null;
  }

  /**
   * Strength of the image-based lighting, `1` being the authored map. See
   * {@link DEFAULT_SKYBOX_LIGHTING}; the dev stats panel drives this.
   */
  get skyboxLighting(): number {
    return this.m_skyboxLighting;
  }

  setSkyboxLighting(intensity: number): void {
    const clamped = Math.min(
      MAX_SKYBOX_LIGHTING,
      Math.max(MIN_SKYBOX_LIGHTING, Number.isFinite(intensity) ? intensity : DEFAULT_SKYBOX_LIGHTING),
    );
    if (clamped === this.m_skyboxLighting) return;
    this.m_skyboxLighting = clamped;
    if (this.m_engine) {
      this.m_engine.scene.environmentIntensity = CAMPFIRE_SKYBOX_ENCODE_SCALE * clamped;
    }
    this.requestRender();
    writeSkyboxLightingPref(clamped);
  }

  private async configureCampfireEnvironment(): Promise<void> {
    const engine = this.m_engine;
    const pmrem = this.m_pmrem;
    if (!engine || !pmrem) return;

    try {
      const equirect = await new TextureLoader().loadAsync(CAMPFIRE_SKYBOX_URL);
      if (this.m_disposed || this.m_engine !== engine) {
        equirect.dispose();
        return;
      }
      equirect.colorSpace = SRGBColorSpace;
      equirect.mapping = EquirectangularReflectionMapping;
      // The image-texture default, and what `HDRLoader` forced on the `.hdr`
      // this JPEG replaced — see the encode-scale comment at the top of this
      // file for why `false` uploads the studio map upside down.
      equirect.flipY = true;

      const env = pmrem.fromEquirectangular(equirect).texture;
      equirect.dispose();
      this.m_environment = env;
      engine.scene.environment = env;
      this.requestRender();
    } catch (err) {
      console.warn("[EngineService] failed to load Campfire skybox lighting", err);
    }
  }

  private clearCampfireEnvironment(): void {
    if (this.m_engine) this.m_engine.scene.environment = null;
    this.m_environment?.dispose();
    this.m_environment = null;
    this.m_pmrem?.dispose();
    this.m_pmrem = null;
  }
}

/** The stage's `updateMatrixWorld` while the pipeline runs — see `renderPipelineWithOneMatrixWalk`. */
function skipMatrixWalk(): void {}

/**
 * The device ratio is capped BEFORE the render scale is applied, so the
 * scale reads the same on a 1× monitor and a retina one — it is a quality
 * dial, not a second display-density term. The product then gets its own
 * ceiling and floor (see `renderQuality.ts`).
 */
function getCanvasPixelRatio(renderScale: number): number {
  const device = Math.max(1, Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO));
  return Math.max(
    MIN_EFFECTIVE_PIXEL_RATIO,
    Math.min(device * renderScale, MAX_EFFECTIVE_PIXEL_RATIO),
  );
}

/**
 * Read at construction rather than pushed in by the panel: the stats panel is
 * debug-gated and usually shut, and a stored override has to reach the scene
 * on a plain reload or the developer loses their place between experiments.
 */
function readSkyboxLightingPref(): number {
  try {
    // Absence is the default, NOT zero — `Number(null)` is a finite 0, which
    // would ship every first-time load with the environment light switched off.
    const raw = window.localStorage.getItem(SKYBOX_LIGHTING_PREF_KEY);
    if (raw === null) return DEFAULT_SKYBOX_LIGHTING;
    const stored = Number(raw);
    if (!Number.isFinite(stored)) return DEFAULT_SKYBOX_LIGHTING;
    return Math.min(MAX_SKYBOX_LIGHTING, Math.max(MIN_SKYBOX_LIGHTING, stored));
  } catch {
    return DEFAULT_SKYBOX_LIGHTING;
  }
}

function writeSkyboxLightingPref(intensity: number): void {
  try {
    if (intensity === DEFAULT_SKYBOX_LIGHTING) {
      window.localStorage.removeItem(SKYBOX_LIGHTING_PREF_KEY);
    } else {
      window.localStorage.setItem(SKYBOX_LIGHTING_PREF_KEY, String(intensity));
    }
  } catch {
    /* private-mode storage refusal is not worth surfacing */
  }
}

export const ENGINE_SERVICE = Symbol.for("EngineService");
